import { useSyncExternalStore } from "react";

const STORAGE_KEY = "drawOla-shape-library-pref";
const DEFAULT_ENABLED = true;

const readFromStorage = (): boolean => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "true") {
      return true;
    }
    if (raw === "false") {
      return false;
    }
  } catch {
    // localStorage may be blocked (private mode, etc.); fall back to default.
  }
  return DEFAULT_ENABLED;
};

let cachedEnabled: boolean = readFromStorage();
const listeners = new Set<() => void>();

const notify = () => {
  for (const listener of listeners) {
    listener();
  }
};

const setEnabled = (next: boolean) => {
  if (cachedEnabled === next) {
    return;
  }
  cachedEnabled = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next ? "true" : "false");
  } catch {
    // Ignore write failures; the in-memory state still works for the session.
  }
  notify();
};

if (typeof window !== "undefined") {
  window.addEventListener("storage", (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY) {
      return;
    }
    const next =
      e.newValue === "true"
        ? true
        : e.newValue === "false"
        ? false
        : DEFAULT_ENABLED;
    if (cachedEnabled !== next) {
      cachedEnabled = next;
      notify();
    }
  });
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => cachedEnabled;

export const useShapeLibraryPreference = (): [
  boolean,
  (next: boolean) => void,
] => {
  const enabled = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return [enabled, setEnabled];
};
